import React, { useRef, useState } from 'react';
import { parserService } from '../services/api';
import type { ParsedData } from '../types';
import toast from 'react-hot-toast';
import { Loader2, Upload } from 'lucide-react';

interface ResumeImportButtonProps {
  onImported: (data: ParsedData) => void;
}

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp';
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const getImportErrorMessage = (error: unknown): string => {
  const status = (error as { response?: { status?: number } }).response?.status;
  if (status === 429 || status === 503) {
    return 'The AI parsing service is temporarily busy (quota exceeded). Please try again in a few minutes.';
  }
  if (status === 500) {
    return 'Something went wrong while processing your resume. Please try again.';
  }
  const axiosError = error as { response?: { data?: { detail?: string } }; message?: string };
  return axiosError.response?.data?.detail || axiosError.message || 'Failed to import resume';
};

const ResumeImportButton: React.FC<ResumeImportButtonProps> = ({ onImported }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);

  const handleFile = async (file: File) => {
    const ext = ('.' + file.name.toLowerCase().split('.').pop() || '');
    const allowedExt = ACCEPT.split(',').some((a) => a === ext);
    if (!allowedExt && !ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Please upload a PDF or image file (JPG, PNG, WEBP)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setIsImporting(true);
    try {
      const result = await parserService.uploadAndParse(file);
      if (result?.success && result.data) {
        onImported(result.data);
      } else {
        toast.error(String(result?.error || 'Failed to parse resume'));
      }
    } catch (error) {
      console.error('Resume import error:', error);
      toast.error(getImportErrorMessage(error));
    } finally {
      setIsImporting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    e.target.value = '';
    if (selected) void handleFile(selected);
  };

  return (
    <>
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={handleChange} />
      <button
        type="button"
        className="h-8 px-3 border border-zinc-200 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-1.5 shrink-0"
        onClick={() => inputRef.current?.click()}
        disabled={isImporting}
        title="Import experience, education and skills from a resume file"
      >
        {isImporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{isImporting ? 'Importing...' : 'Import resume'}</span>
        <span className="sm:hidden">{isImporting ? '...' : 'Import'}</span>
      </button>
    </>
  );
};

export default ResumeImportButton;